/** XeAgents.ai - Voice Library Page **/
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { Play, Mic, Square, Trash2, Upload, StopCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from 'react-i18next';
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import NovaSonicVoiceLibrary from "@/components/NovaSonicVoiceLibrary";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Voice {
  voice_id: string;
  name: string;
  category?: string;
  labels?: Record<string, string>;
  preview_url?: string;
}

interface SharedVoice {
  voice_id: string;
  public_owner_id: string;
  name: string;
  description?: string;
  category?: string;
  labels?: Record<string, string>;
  accent?: string;
  age?: string;
  gender?: string;
  language?: string;
  use_case?: string;
  preview_url?: string;
}

export default function Voices() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [browseLanguageFilter, setBrowseLanguageFilter] = useState<string>('en-us');
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("my-voices");
  const [addingVoices, setAddingVoices] = useState<Set<string>>(new Set());
  const [voiceToDelete, setVoiceToDelete] = useState<{ id: string; name: string; language?: string } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Voice cloning state
  const [cloneVoiceName, setCloneVoiceName] = useState("");
  const [cloneVoiceDescription, setCloneVoiceDescription] = useState("");
  const [cloneVoiceGender, setCloneVoiceGender] = useState<string>("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Voice engine the super admin chose: Amazon Nova Sonic (default) or ElevenLabs
  const { data: voiceEngine, isLoading: engineLoading } = useQuery<{ provider: string }>({
    queryKey: ["/api/public/voice-provider"],
    staleTime: 30_000,
  });

  // Fetch user's organization data to determine country
  const { data: userData } = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/auth/me');
      return res.json();
    },
  });

  const { data: orgData } = useQuery({
    queryKey: ['/api/organizations/me'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/organizations/me');
      return res.json();
    },
    enabled: !!userData?.organizationId,
  });

  // Check if organization or billing country is India
  const isIndiaUser = useMemo(() => {
    const billingCountry = userData?.billingCountry?.toUpperCase();
    const orgCountry = userData?.organizationCountry?.toUpperCase();
    const orgDataCountry = orgData?.organization?.country?.toUpperCase();

    return (
      billingCountry === 'IN' ||
      billingCountry === 'INDIA' ||
      orgCountry === 'IN' ||
      orgCountry === 'INDIA' ||
      orgDataCountry === 'IN' ||
      orgDataCountry === 'INDIA'
    );
  }, [userData?.billingCountry, userData?.organizationCountry, orgData?.organization?.country]);

  // Set default language filter based on user's country
  useEffect(() => {
    if (isIndiaUser) {
      setBrowseLanguageFilter('en-in'); // Default to Indian English for Indian users
    }
  }, [isIndiaUser]);

  // ============================================
  // FETCH USER'S SAVED VOICES (LOCAL DATABASE)
  // ============================================

  // Fetch ElevenLabs saved voices (My Voices)
  const { data: savedVoices, isLoading: isLoadingSavedVoices } = useQuery<Voice[]>({
    queryKey: ["/api/agents/elevenlabs/user-voices"],
    staleTime: 60000,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/agents/elevenlabs/user-voices");
      const data = await res.json();
      const voices = Array.isArray(data) ? data : (data.voices || []);
      return voices.map((v: any) => ({
        voice_id: v.voice_id || v.elevenlabsVoiceId || v.elevenLabsVoiceId,
        name: v.name || v.displayName,
        category: v.category || undefined,
        labels: v.labels || {
          language: v.accent || v.language || '',
          gender: v.gender || '',
          accent: v.accent || '',
          age: v.age || '',
          description: v.description || '',
          use_case: v.useCase || v.use_case || '',
        } as Record<string, string>,
        preview_url: v.preview_url || undefined,
      }));
    },
  });

  // ============================================
  // BROWSE LIBRARY (ELEVENLABS)
  // ============================================

  // Fetch ElevenLabs voices from catalog (Browse Library)
  // Backend filters based on language parameter (en-in for Indian English, en-us for Native English)
  const { data: elevenLabsCatalog, isLoading: isLoadingCatalog } = useQuery<Voice[]>({
    queryKey: ["/api/agents/elevenlabs/voices", browseLanguageFilter],
    enabled: activeTab === "browse-library",
    staleTime: 300000,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/agents/elevenlabs/voices?language=${browseLanguageFilter}`);
      return res.json();
    },
  });

  // Convert catalog to SharedVoice format
  const sharedVoices = useMemo(() => {
    return (elevenLabsCatalog || []).map(v => ({
      ...v,
      public_owner_id: "elevenlabs",
      description: v.labels?.description,
      language: v.labels?.language,
      gender: v.labels?.gender,
      accent: v.labels?.accent,
    } as SharedVoice));
  }, [elevenLabsCatalog]);

  // ============================================
  // MUTATIONS
  // ============================================

  // Add voice to ElevenLabs local library
  const addVoiceMutation = useMutation({
    mutationFn: async ({ voice }: { voice: Voice }) => {
      const response = await apiRequest(
        "POST",
        "/api/agents/elevenlabs/voices/add",
        {
          elevenlabsVoiceId: voice.voice_id,
          displayName: voice.name,
          gender: voice.labels?.gender || null,
          accent: voice.labels?.accent || null,
          age: voice.labels?.age || null,
          category: voice.category || null,
          useCase: voice.labels?.use_case || null,
          description: voice.labels?.description || null,
          languageCode: voice.labels?.language || null,
        }
      );
      return response.json();
    },
    onSuccess: (_data, variables) => {
      toast({
        title: "Voice Added",
        description: `${variables.voice.name} has been added to your library`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/agents/elevenlabs/user-voices"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Add Voice",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete ElevenLabs voice from library
  const deleteVoiceMutation = useMutation({
    mutationFn: async (voiceId: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/agents/elevenlabs/user-voices/${voiceId}`
      );
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Voice Removed",
        description: `${voiceToDelete?.name || "Voice"} has been removed from your library`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/agents/elevenlabs/user-voices"] });
      setVoiceToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Remove Voice",
        description: error.message,
        variant: "destructive",
      });
      setVoiceToDelete(null);
    },
  });

  // Clone voice mutation
  const cloneVoiceMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await fetch('/api/agents/elevenlabs/voices/clone', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to clone voice');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Voice Cloned Successfully!",
        description: data.message || `${data.voice?.name || 'Your voice'} is now available in your library.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/agents/elevenlabs/user-voices"] });
      // Reset form
      setCloneVoiceName("");
      setCloneVoiceDescription("");
      setCloneVoiceGender("");
      setAudioFile(null);
      setRecordedBlob(null);
      setRecordingTime(0);
      // Switch to My Voices tab to show the new voice
      setActiveTab("my-voices");
    },
    onError: (error: Error) => {
      toast({
        title: "Voice Cloning Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Recording functions
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      recordingChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordingChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Failed to start recording:', error);
      toast({
        title: "Recording Failed",
        description: "Could not access microphone. Please check your permissions.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, [isRecording]);

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCloneVoice = async () => {
    if (!cloneVoiceName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your cloned voice.",
        variant: "destructive",
      });
      return;
    }

    const audioSource = audioFile || recordedBlob;
    if (!audioSource) {
      toast({
        title: "Audio Required",
        description: "Please upload an audio file or record your voice.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append('name', cloneVoiceName.trim());
    if (cloneVoiceDescription.trim()) {
      formData.append('description', cloneVoiceDescription.trim());
    }
    if (cloneVoiceGender) {
      formData.append('gender', cloneVoiceGender);
    }

    // Append audio file
    if (audioFile) {
      formData.append('audio', audioFile);
    } else if (recordedBlob) {
      formData.append('audio', recordedBlob, 'recorded-voice.webm');
    }

    cloneVoiceMutation.mutate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAudioFile(file);
      setRecordedBlob(null); // Clear recorded audio if file is uploaded
    }
  };

  const clearAudio = () => {
    setAudioFile(null);
    setRecordedBlob(null);
    setRecordingTime(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddVoice = async (voice: SharedVoice | Voice) => {
    setAddingVoices(prev => new Set(prev).add(voice.voice_id));
    try {
      await addVoiceMutation.mutateAsync({ voice: voice as Voice });
    } finally {
      setAddingVoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(voice.voice_id);
        return newSet;
      });
    }
  };

  // Check if a shared voice is already in user's account
  const isVoiceAdded = (voiceId: string) => {
    if (!savedVoices) return false;
    return savedVoices.some(v => v.voice_id === voiceId);
  };

  // ============================================
  // FILTERING
  // ============================================

  // For My Voices: Extract languages only from user's saved voices
  const availableLanguages = useMemo(() => {
    if (!savedVoices) return [];

    const languageSet = new Set<string>();
    savedVoices.forEach(voice => {
      const lang = (voice.labels?.language || voice.labels?.accent || '').toLowerCase();
      if (lang) {
        const langCode = lang.split('-')[0];
        languageSet.add(langCode);
      }
    });

    return Array.from(languageSet).sort();
  }, [savedVoices]);

  // ElevenLabs supported languages for Browse Library
  const availableBrowseLanguages = useMemo(() => {
    if (isIndiaUser) {
      // Indian orgs: Indian English + Indian languages + Native English
      return ['en-in', 'hi', 'ta', 'te', 'bn', 'mr', 'gu', 'kn', 'ml', 'pa', 'en-us'];
    }
    // Non-Indian (US) orgs: Native English + World languages (no Indian languages)
    return [
      'en-us', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'tr', 'ru', 'nl',
      'cs', 'ar', 'zh', 'hu', 'ko', 'ja', 'sv', 'id', 'fil', 'ms',
      'ro', 'uk', 'el', 'bg', 'hr', 'sk', 'da', 'fi', 'no', 'vi', 'th'
    ];
  }, [isIndiaUser]);

  const filteredSavedVoices = useMemo(() => {
    if (!savedVoices) return [];

    let voices = savedVoices;

    // Apply language filter
    if (languageFilter !== 'all') {
      voices = voices.filter(v => {
        const lang = (v.labels?.language || v.labels?.accent || '').toLowerCase();
        const langCode = lang.split('-')[0];
        return langCode === languageFilter.toLowerCase();
      });
    }

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      voices = voices.filter(v =>
        v.name.toLowerCase().includes(searchLower) ||
        v.labels?.language?.toLowerCase().includes(searchLower) ||
        v.labels?.gender?.toLowerCase().includes(searchLower) ||
        v.labels?.accent?.toLowerCase().includes(searchLower) ||
        v.category?.toLowerCase().includes(searchLower)
      );
    }

    return voices;
  }, [savedVoices, debouncedSearch, languageFilter]);

  const filteredSharedVoices = useMemo(() => {
    if (!sharedVoices) return [];

    let voices = sharedVoices;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      voices = voices.filter(v =>
        v.name.toLowerCase().includes(searchLower) ||
        v.description?.toLowerCase().includes(searchLower) ||
        v.language?.toLowerCase().includes(searchLower) ||
        v.gender?.toLowerCase().includes(searchLower) ||
        v.accent?.toLowerCase().includes(searchLower) ||
        v.use_case?.toLowerCase().includes(searchLower) ||
        v.category?.toLowerCase().includes(searchLower)
      );
    }

    return voices;
  }, [sharedVoices, debouncedSearch]);

  // ============================================
  // AUDIO PLAYBACK
  // ============================================

  const handlePlayPreview = async (voice: Voice | SharedVoice) => {
    // Stop if already playing this voice
    if (playingVoice === voice.voice_id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setPlayingVoice(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    try {
      setPlayingVoice(voice.voice_id);

      // Use backend endpoint to generate preview audio
      const response = await apiRequest('POST', '/api/agents/elevenlabs/test-voice', {
        voiceId: voice.voice_id,
        language: browseLanguageFilter,
      });

      if (!response.ok) {
        throw new Error('Failed to generate voice preview');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.play();
      audio.onended = () => {
        setPlayingVoice(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };
      audio.onerror = () => {
        setPlayingVoice(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        toast({
          title: 'Preview Failed',
          description: 'Could not play voice preview. Please try again.',
          variant: 'destructive',
        });
      };
    } catch (error) {
      console.error('Failed to play voice preview:', error);
      setPlayingVoice(null);
      toast({
        title: 'Preview Failed',
        description: 'Could not play voice preview. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // ============================================
  // UTILITIES
  // ============================================

  const formatLanguageName = (code: string) => {
    const languageNames: Record<string, string> = {
      'en-in': "Indian English", 'en-us': "Native English", en: "English",
      es: "Spanish", fr: "French", de: "German", it: "Italian",
      pt: "Portuguese", ja: "Japanese", ko: "Korean", zh: "Chinese", ru: "Russian",
      ar: "Arabic", hi: "Hindi", nl: "Dutch", pl: "Polish", sv: "Swedish",
      tr: "Turkish", id: "Indonesian", th: "Thai", vi: "Vietnamese", cs: "Czech",
      el: "Greek", hu: "Hungarian", ro: "Romanian", uk: "Ukrainian", he: "Hebrew",
      ms: "Malay", fil: "Filipino", da: "Danish", fi: "Finnish", no: "Norwegian",
      sk: "Slovak", bg: "Bulgarian", hr: "Croatian", lt: "Lithuanian", lv: "Latvian",
      sl: "Slovenian", et: "Estonian", od: "Odia", or: "Odia",
      // Indian languages
      bn: "Bengali", ta: "Tamil", te: "Telugu", kn: "Kannada",
      ml: "Malayalam", mr: "Marathi", gu: "Gujarati", pa: "Punjabi",
      as: "Assamese",
    };
    return languageNames[code?.toLowerCase()] || code?.toUpperCase() || "Unknown";
  };

  const getSubtitle = () => {
    if (activeTab === "my-voices") {
      if (isLoadingSavedVoices) return t('voices.subtitleLoading');
      return `${savedVoices?.length || 0} voices available`;
    }
    return `${filteredSharedVoices.length} voices available`;
  };

  // ============================================
  // RENDER
  // ============================================

  // The super admin's voice engine decides which library this page shows (Admin -> Global Settings).
  if (engineLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }
  if (voiceEngine?.provider === "nova-sonic") return <NovaSonicVoiceLibrary />;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-pink-50 via-rose-100/50 to-fuchsia-50 dark:from-pink-950/40 dark:via-rose-900/30 dark:to-fuchsia-950/40 border border-pink-100 dark:border-pink-900/50 p-6 md:p-8">
        <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-700/20 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.5))]" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/25">
              <Mic className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('voices.title')}</h1>
              <p className="text-muted-foreground mt-0.5">{getSubtitle()}</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger
            value="my-voices"
            className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
          >
            My Voices
          </TabsTrigger>
          <TabsTrigger
            value="browse-library"
            className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
          >
            Browse Library
          </TabsTrigger>
          <TabsTrigger
            value="clone-voice"
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white"
          >
            Clone Voice
          </TabsTrigger>
        </TabsList>

        <div className="space-y-4">
          {/* Language Selector */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Language Filter Dropdown - My Voices */}
            {activeTab === "my-voices" && availableLanguages.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Language:</label>
                <Select value={languageFilter} onValueChange={setLanguageFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">All Languages ({savedVoices?.length || 0})</SelectItem>
                    {availableLanguages.map(lang => {
                      const count = savedVoices?.filter(v => {
                        const voiceLang = (v.labels?.language || v.labels?.accent || '').toLowerCase();
                        const langCode = voiceLang.split('-')[0];
                        return langCode === lang;
                      }).length || 0;

                      return (
                        <SelectItem key={lang} value={lang}>
                          {formatLanguageName(lang)} ({count})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Language Filter Dropdown - Browse Library */}
            {activeTab === "browse-library" && availableBrowseLanguages.length > 1 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Language:</label>
                <Select value={browseLanguageFilter} onValueChange={setBrowseLanguageFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {availableBrowseLanguages.map(lang => (
                      <SelectItem key={lang} value={lang}>
                        {formatLanguageName(lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {!isLoadingSavedVoices && activeTab === "my-voices" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {languageFilter !== 'all'
                  ? `Showing ${filteredSavedVoices.length} of ${savedVoices?.length || 0} voices • ${formatLanguageName(languageFilter)}`
                  : `${savedVoices?.length || 0} voices available`}
              </span>
            </div>
          )}

          {activeTab === "browse-library" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {debouncedSearch
                  ? `Showing ${filteredSharedVoices.length} of ${sharedVoices.length} voices`
                  : `${filteredSharedVoices.length} ${formatLanguageName(browseLanguageFilter)} voices available`}
              </span>
            </div>
          )}
        </div>

        <TabsContent value="my-voices" className="mt-4">
          {isLoadingSavedVoices ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-5 w-12" />
                      </div>
                    </div>
                    <Skeleton className="h-9 w-9 rounded-md" />
                  </div>
                </Card>
              ))}
            </div>
          ) : filteredSavedVoices.length === 0 ? (
            <Card className="p-16 text-center">
              <Mic className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                {debouncedSearch
                  ? t('voices.noVoicesMatch', 'No voices match your search')
                  : t('voices.noVoicesAvailable', 'No voices saved')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {debouncedSearch
                  ? t('voices.tryDifferentSearch', 'Try a different search term')
                  : 'Add voices from the Browse Library tab'}
              </p>
              <Button onClick={() => setActiveTab("browse-library")} variant="outline">
                Browse Voice Library
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredSavedVoices.map((voice) => {
                const isFemale = voice.labels?.gender?.toLowerCase() === 'female';
                const borderColor = isFemale
                  ? "border-pink-200 dark:border-pink-800/50"
                  : "border-blue-200 dark:border-blue-800/50";
                const gradientBar = isFemale
                  ? "bg-gradient-to-r from-pink-400 via-rose-500 to-pink-400"
                  : "bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400";

                return (
                  <Card
                    key={voice.voice_id}
                    className={`p-4 hover-elevate relative overflow-visible ${borderColor}`}
                    data-testid={`card-voice-${voice.voice_id}`}
                  >
                    <div className={`absolute top-0 left-0 right-0 h-1 ${gradientBar} rounded-t-lg`} />

                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate mb-1.5">
                          {voice.name}
                        </h3>
                        {voice.labels?.description && (
                          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                            {voice.labels.description}
                          </p>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {voice.labels?.gender && (
                            <Badge variant="outline" className="text-xs capitalize">
                              {voice.labels.gender}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mt-3">
                      {/* Test Voice button */}
                      <Button
                        variant={playingVoice === voice.voice_id ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePlayPreview(voice)}
                        className={playingVoice === voice.voice_id
                          ? (isFemale
                            ? "w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                            : "w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white")
                          : (isFemale
                            ? "w-full border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-950"
                            : "w-full border-green-300 hover:bg-green-50 dark:hover:bg-green-950")}
                        data-testid="button-play-voice"
                      >
                        {playingVoice === voice.voice_id ? (
                          <>
                            <Square className="h-4 w-4 mr-2" />
                            Stop Preview
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Test Voice
                          </>
                        )}
                      </Button>

                      {/* Remove button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setVoiceToDelete({
                          id: voice.voice_id,
                          name: voice.name,
                          language: voice.labels?.language,
                        })}
                        className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </Button>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        {voice.category && (
                          <Badge className={`text-xs capitalize ${isFemale ? 'bg-gradient-to-r from-pink-500 to-rose-500' : 'bg-gradient-to-r from-blue-500 to-indigo-500'} text-white border-0`}>
                            {voice.category}
                          </Badge>
                        )}
                        {voice.labels?.age && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {voice.labels.age}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="browse-library" className="mt-4">
          {!isLoadingCatalog && (
            <div className="space-y-4">
              {filteredSharedVoices.length === 0 ? (
                <Card className="p-16 text-center">
                  <Mic className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No voices found</h3>
                  <p className="text-muted-foreground">
                    {debouncedSearch ? 'Try a different search term' : 'No voices available'}
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredSharedVoices.map((voice) => {
                    const alreadyAdded = isVoiceAdded(voice.voice_id);
                    const isAdding = addingVoices.has(voice.voice_id);
                    const isFemale = voice.gender?.toLowerCase() === 'female';
                    const borderColor = isFemale
                      ? "border-pink-200 dark:border-pink-800/50"
                      : "border-blue-200 dark:border-blue-800/50";
                    const gradientBar = isFemale
                      ? "bg-gradient-to-r from-pink-400 via-rose-500 to-pink-400"
                      : "bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400";

                    return (
                      <Card
                        key={voice.voice_id}
                        className={`p-4 hover-elevate relative overflow-visible ${borderColor}`}
                      >
                        <div className={`absolute top-0 left-0 right-0 h-1 ${gradientBar} rounded-t-lg`} />

                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate mb-1.5">
                              {voice.name}
                            </h3>
                            {voice.description && (
                              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                                {voice.description}
                              </p>
                            )}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {voice.gender && (
                                <Badge variant="outline" className="text-xs capitalize">
                                  {voice.gender}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 mt-3">
                          {/* Play button */}
                          <Button
                            variant={playingVoice === voice.voice_id ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePlayPreview(voice)}
                            className={playingVoice === voice.voice_id
                              ? (isFemale
                                ? "w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                                : "w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white")
                              : (isFemale
                                ? "w-full border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-950"
                                : "w-full border-green-300 hover:bg-green-50 dark:hover:bg-green-950")}
                          >
                            {playingVoice === voice.voice_id ? (
                              <>
                                <Square className="h-4 w-4 mr-2" />
                                Stop Preview
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-2" />
                                Test Voice
                              </>
                            )}
                          </Button>

                          {/* Add/Added button */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {voice.category && (
                                <Badge className={`text-xs capitalize ${isFemale ? 'bg-gradient-to-r from-pink-500 to-rose-500' : 'bg-gradient-to-r from-blue-500 to-indigo-500'} text-white border-0`}>
                                  Multilingual
                                </Badge>
                              )}
                            </div>
                            {alreadyAdded ? (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                ✓ Added
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleAddVoice(voice)}
                                disabled={isAdding}
                                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                              >
                                {isAdding ? 'Adding...' : '+ Add'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isLoadingCatalog && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <Card key={i} className="p-4">
                  <Skeleton className="h-5 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <div className="flex gap-2 mb-3">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                  <Skeleton className="h-9 w-full" />
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Clone Voice Tab */}
        <TabsContent value="clone-voice" className="mt-4">
          <Card className="p-6 max-w-2xl">
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center">
                  <Mic className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Clone Your Voice</h2>
                  <p className="text-sm text-muted-foreground">
                    Create a custom AI voice using your own voice sample
                  </p>
                </div>
              </div>

              {/* Voice Name */}
              <div className="space-y-2">
                <Label htmlFor="voice-name">Voice Name *</Label>
                <Input
                  id="voice-name"
                  placeholder="e.g., My Custom Voice"
                  value={cloneVoiceName}
                  onChange={(e) => setCloneVoiceName(e.target.value)}
                  maxLength={50}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="voice-description">Description (Optional)</Label>
                <Textarea
                  id="voice-description"
                  placeholder="Describe your voice characteristics..."
                  value={cloneVoiceDescription}
                  onChange={(e) => setCloneVoiceDescription(e.target.value)}
                  maxLength={200}
                  rows={2}
                />
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label>Gender (Optional)</Label>
                <div className="flex items-center gap-2">
                  <Select value={cloneVoiceGender} onValueChange={setCloneVoiceGender}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                    </SelectContent>
                  </Select>
                  {cloneVoiceGender && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCloneVoiceGender("")}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Audio Source */}
              <div className="space-y-4">
                <Label>Audio Sample *</Label>
                <p className="text-sm text-muted-foreground">
                  Provide at least 1 minute of clear speech. No background music or noise.
                </p>

                {/* Recording OR File Upload */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Record Audio Card */}
                  <Card className="p-4 border-2 border-dashed hover:border-purple-300 transition-colors">
                    <div className="text-center space-y-3">
                      <div className="flex justify-center">
                        <div className={`h-16 w-16 rounded-full flex items-center justify-center transition-all ${isRecording ? 'bg-red-100 animate-pulse' : 'bg-purple-100'}`}>
                          <Mic className={`h-8 w-8 ${isRecording ? 'text-red-600' : 'text-purple-600'}`} />
                        </div>
                      </div>
                      <h3 className="font-medium">Record Audio</h3>
                      {isRecording ? (
                        <div className="space-y-2">
                          <div className="text-2xl font-mono text-red-600">
                            {formatTime(recordingTime)}
                          </div>
                          <Progress value={Math.min((recordingTime / 60) * 100, 100)} className="h-2" />
                          <p className="text-xs text-muted-foreground">
                            {recordingTime < 60 ? `${60 - recordingTime}s more needed` : 'Good! You can stop now'}
                          </p>
                          <Button
                            onClick={stopRecording}
                            variant="destructive"
                            className="w-full"
                          >
                            <StopCircle className="h-4 w-4 mr-2" />
                            Stop Recording
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={startRecording}
                          disabled={!!audioFile}
                          className="w-full bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600"
                        >
                          <Mic className="h-4 w-4 mr-2" />
                          Start Recording
                        </Button>
                      )}
                      {recordedBlob && !isRecording && (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-950 rounded-lg">
                          <p className="text-sm text-green-600 dark:text-green-400">
                            Recorded: {formatTime(recordingTime)}
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Upload File Card */}
                  <Card className="p-4 border-2 border-dashed hover:border-blue-300 transition-colors">
                    <div className="text-center space-y-3">
                      <div className="flex justify-center">
                        <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                          <Upload className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>
                      <h3 className="font-medium">Upload File</h3>
                      <p className="text-xs text-muted-foreground">
                        MP3, WAV, M4A (max 25MB)
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="audio/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="audio-upload"
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!!recordedBlob}
                        variant="outline"
                        className="w-full border-blue-300 hover:bg-blue-50"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </Button>
                      {audioFile && (
                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
                          <p className="text-sm text-blue-600 dark:text-blue-400 truncate">
                            {audioFile.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Clear Audio Button */}
                {(audioFile || recordedBlob) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAudio}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear Audio
                  </Button>
                )}
              </div>

              {/* Tips */}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Tips for best results:</h4>
                <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
                  <li>Record in a quiet environment</li>
                  <li>Speak clearly and naturally</li>
                  <li>Avoid background music or noise</li>
                  <li>1 minute minimum, 3+ minutes ideal</li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleCloneVoice}
                disabled={cloneVoiceMutation.isPending || !cloneVoiceName.trim() || (!audioFile && !recordedBlob)}
                className="w-full bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 h-12 text-lg"
              >
                {cloneVoiceMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Cloning Voice...
                  </>
                ) : (
                  <>
                    <Mic className="h-5 w-5 mr-2" />
                    Clone My Voice
                  </>
                )}
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!voiceToDelete} onOpenChange={() => setVoiceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Voice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{voiceToDelete?.name}" from your library?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteVoiceMutation.isPending}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (voiceToDelete) {
                  deleteVoiceMutation.mutate(voiceToDelete.id);
                }
              }}
              disabled={deleteVoiceMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteVoiceMutation.isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
